import { match } from 'ts-pattern';
import { z } from 'zod';

import {
  DEPOSIT_APPLE_PAY_B2B_LIMIT,
  DEPOSIT_CARD_B2B_LIMIT,
  DEPOSIT_REGULAR_BANK_TRANSFER_LIMIT,
  DEPOSIT_SEPA_DD_LIMIT,
  FULLY_MANAGED_ORDER_LIMIT,
} from '@Common/constants/b2b/limits';
import { ZodAmount } from '@Common/types/Amount';
import { ZodTaigaId } from '@Common/types/b2b/axa/common';
import { ZodTaigaSavingPlanId, ZodTaigaSavingPlanIdPathParam } from '@Common/types/b2b/axa/plans';
import { ZodB2BFundingSource, ZodFundingSourceV2 } from '@Common/types/Funding';
import { CARD_TOPUP_MAX_AMOUNT } from '@Common/types/funding/limits/deposit';
import { PaymentMethod, ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import { ZodIDPathParam } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { zodMaskedString } from '@Common/types/zod/zod-masked-string';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const CoCardTopUpsStatusValues = [
  'initiated',
  'pending',
  'captured',
  'rejected',
  'settled',
  'authorization_declined',
  'shares_cancelled',
  'pending_declined',
  'card_verified',
  'card_verification_declined',
  'returned',
  'refunded',
  'partially_refunded',
] as const;

export const ZodCoCardTopUpsStatus = z.enum(CoCardTopUpsStatusValues);

export type CoCardTopUpsStatus = z.infer<typeof ZodCoCardTopUpsStatus>;

const ZodGetCardTopUpResponse = z.object({
  id: z.number(),
  amount: ZodAmount,
  card_id: z.number().nullable(),
  status: ZodCoCardTopUpsStatus.nullable(),
  pending_funds_settled: z.boolean(),
  message: ZodTrimmedString.nullable(),
});

export type GetCardTopUpResponse = z.infer<typeof ZodGetCardTopUpResponse>;

export function validateCorrelationIdForSavingContext({
  allowUnscheduledPayment = false,
}: {
  allowUnscheduledPayment?: boolean;
} = {}) {
  return function validateCorrelationIdForSavingContextHandler({
    deposit_context,
    is_unscheduled_payment,
    correlation_id,
  }: {
    deposit_context: 'regular' | 'savings' | 'pea';
    is_unscheduled_payment?: boolean;
    correlation_id?: string;
  }) {
    return (
      ['regular', 'pea'].includes(deposit_context) ||
      !!correlation_id ||
      (allowUnscheduledPayment && is_unscheduled_payment && !correlation_id)
    );
  };
}

export function validateDepositAmount(paymentMethod: PaymentMethod) {
  return function validate({
    deposit_context,
    amount,
    is_deposit_limit_raised = false,
  }: {
    deposit_context: 'regular' | 'savings' | 'pea';
    amount: number;
    is_deposit_limit_raised?: boolean;
  }) {
    if (deposit_context === 'savings') {
      return match(paymentMethod)
        .with('card', () =>
          is_deposit_limit_raised
            ? amount <= FULLY_MANAGED_ORDER_LIMIT
            : amount <= DEPOSIT_CARD_B2B_LIMIT,
        )
        .with('apple-pay', () => amount <= DEPOSIT_APPLE_PAY_B2B_LIMIT)
        .with('regular-bank', () => amount <= DEPOSIT_REGULAR_BANK_TRANSFER_LIMIT)
        .with('sepa-direct-debit', () => amount <= DEPOSIT_SEPA_DD_LIMIT)
        .exhaustive();
    }

    return match(paymentMethod)
      .with('card', () =>
        is_deposit_limit_raised
          ? amount <= FULLY_MANAGED_ORDER_LIMIT
          : amount <= CARD_TOPUP_MAX_AMOUNT.DEFAULT.value,
      )
      .with('apple-pay', () => amount <= CARD_TOPUP_MAX_AMOUNT.DEFAULT.value)
      .with('sepa-direct-debit', () => amount <= DEPOSIT_SEPA_DD_LIMIT)
      .with('regular-bank', () => true)
      .exhaustive();
  };
}

export const getFundingDepositCardTopUpsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetCardTopUpResponse,
});

export type GetFundingDepositCardTopUpsIdEndpoint = GetEndpointType<
  typeof getFundingDepositCardTopUpsIdEndpoint
>;

export const postFundingDepositPaymentMethodEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: z.object({
    payment_method: ZodB2BPaymentMethod.nullable(),
  }),
  responseBody: ZodSuccessResponse,
  headers: undefined,
});

export type PostFundingDepositPaymentMethodEndpoint = GetEndpointType<
  typeof postFundingDepositPaymentMethodEndpoint
>;

const ZodPostFundingDepositTaigaBody = z.object({
  correlation_id: ZodTaigaId,
  saving_plan_id: ZodTaigaSavingPlanId,
});

export const postFundingDepositTaigaEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: z.object({
    ...ZodPostFundingDepositTaigaBody.shape,
    amount: z.number(),
    payment_method: z.literal('card'),
  }),
  responseBody: z.object({
    transaction_id: ZodTaigaId,
    url: z.url(),
    token: ZodTrimmedString,
  }),
  headers: undefined,
});

export type PostFundingDepositTaigaEndpoint = GetEndpointType<
  typeof postFundingDepositTaigaEndpoint
>;

export const ZodPostFundingDepositTaigaCardTokenParams = createZodParamsValidator(
  z.object({
    token: ZodTrimmedString,
  }),
);

export const postFundingDepositTaigaCardTokenEndpoint = endpoint({
  method: 'POST',
  pathParams: ZodPostFundingDepositTaigaCardTokenParams,
  queryParams: undefined,
  body: z.object({
    ...ZodPostFundingDepositTaigaBody.shape,
    funding_source: ZodFundingSourceV2.or(ZodB2BFundingSource).optional(),
    comment: ZodTrimmedString.optional(),
  }),
  responseBody: z.object({
    is_funded: z.boolean(),
  }),
  headers: undefined,
});

export type PostFundingDepositTaigaCardTokenEndpoint = GetEndpointType<
  typeof postFundingDepositTaigaCardTokenEndpoint
>;

const ZodTaigaSepaDirectDebitAccount = z.object({
  rib_id: z.number(),
  iban: zodMaskedString,
  name: ZodTrimmedString.nullable(),
});

export type TaigaSepaDirectDebitAccount = z.infer<typeof ZodTaigaSepaDirectDebitAccount>;

export const ZodGetFundingDepositSavingPlansSavingPlanIdPaymentMethodsParams =
  createZodParamsValidator(
    z.object({
      saving_plan_id: ZodTaigaSavingPlanIdPathParam,
    }),
  );

export const getFundingDepositSavingPlansSavingPlanIdPaymentMethodsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetFundingDepositSavingPlansSavingPlanIdPaymentMethodsParams,
  queryParams: undefined,
  body: undefined,
  responseBody: z.object({
    card: z.object({
      available: z.boolean(),
    }),
    sepa_direct_debit: z.array(ZodTaigaSepaDirectDebitAccount),
  }),
  headers: undefined,
});

export type GetFundingDepositSavingPlansSavingPlanIdPaymentMethodsEndpoint = GetEndpointType<
  typeof getFundingDepositSavingPlansSavingPlanIdPaymentMethodsEndpoint
>;
